import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FieldArray, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { FormValues, Contact } from "../types";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";
import { useGetFeedback360Result } from "../hooks";
const FEEDBACK_360_LIMIT = 40;
interface ContactListProps {
  values: FormValues;
}

const ContactList: React.FC<ContactListProps> = ({ values }) => {
  const { t } = useTranslation("myArea");
  const desktopListRef = useRef<HTMLDivElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const { errors, touched } = useFormikContext<FormValues>();
  const { data: feedbackResult, isLoading } = useGetFeedback360Result();

  // Auto scroll to the bottom of the list when a new contact is added
  const scrollToBottom = () => {
    if (mobileListRef.current) {
      mobileListRef.current.scrollTop = mobileListRef.current.scrollHeight;
    }
    if (desktopListRef.current) {
      desktopListRef.current.scrollTop = desktopListRef.current.scrollHeight;
    }
  };

  // Helper function to check if a specific field has an error
  const hasFieldError = (index: number, field: keyof Contact): boolean => {
    if (!touched.contacts || !errors.contacts) return false;

    // If contacts has a string error, it's an array-level error
    if (typeof errors.contacts === "string") return false;

    // Check if the specific contact and field has an error
    return (
      Array.isArray(touched.contacts) &&
      touched.contacts[index] &&
      (touched.contacts[index] as any)[field] &&
      Array.isArray(errors.contacts) &&
      errors.contacts[index] &&
      !!(errors.contacts[index] as any)[field]
    );
  };

  return (
    <FieldArray name="contacts">
      {({ push, remove }) => (
        <>
          {/* Contact List */}
          <div className="overflow-x-auto">
            {/* Mobile/Tablet View (stacked) */}
            <div className="block md:hidden mb-4">
              <div
                ref={mobileListRef}
                className="max-h-[57rem] overflow-y-auto space-y-6"
                style={{ minHeight: "57rem" }}
              >
                {values.contacts.map((contact, index) => (
                  <div
                    key={contact.id}
                    className={`bg-white rounded-lg relative mr-2 ${
                      contact.disabled ? "opacity-75" : ""
                    }`}
                  >
                    <div className="relative">
                      {/* Delete button - only show for non-disabled contacts beyond the minimum */}
                      {!contact.disabled && index >= 8 && (
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500 z-10"
                          aria-label="Delete contact"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}

                      <div
                        className={`${
                          !contact.disabled && index >= 8 ? "pr-6" : ""
                        }`}
                      >
                        <div className="mb-2">
                          <Field
                            type="email"
                            name={`contacts.${index}.email`}
                            placeholder={t(
                              "assessments.feedback360.email-placeholder"
                            )}
                            disabled={contact.disabled}
                            className={`w-full px-4 rounded-lg shadow-md ${
                              contact.disabled ? "bg-gray-100" : ""
                            } ${
                              hasFieldError(index, "email")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {!contact.disabled && (
                            <ErrorMessage
                              name={`contacts.${index}.email`}
                              component="p"
                              className="mt-1 text-sm text-red-500"
                            />
                          )}
                        </div>

                        <div className="flex items-center">
                          <Field
                            as="select"
                            name={`contacts.${index}.type`}
                            disabled={contact.disabled}
                            className={`w-full px-4 border rounded-lg shadow-md ${
                              contact.disabled ? "bg-gray-100" : ""
                            } ${
                              hasFieldError(index, "type")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            <option value="" disabled className="text-gray-600">
                              {t("assessments.feedback360.select-placeholder")}
                            </option>
                            <option value="employee">
                              {t("assessments.feedback360.employee")}
                            </option>
                            <option value="manager">
                              {t("assessments.feedback360.manager")}
                            </option>
                            <option value="colleague">
                              {t("assessments.feedback360.colleague")}
                            </option>
                            <option value="businesspartner">
                              {t("assessments.feedback360.businesspartner")}
                            </option>
                            <option value="other">
                              {t("assessments.feedback360.other")}
                            </option>
                          </Field>

                          {/* Request date for existing contacts */}
                          {contact.disabled && contact.requestDate && (
                            <div className="ml-2 text-sm whitespace-nowrap">
                              {contact.requestDate}
                            </div>
                          )}
                        </div>

                        {!contact.disabled && (
                          <ErrorMessage
                            name={`contacts.${index}.type`}
                            component="p"
                            className="mt-1 text-sm text-red-500"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop View (N columns) */}
            <div className="hidden md:block">
              <div
                ref={desktopListRef}
                className="max-h-[27rem] overflow-y-auto rounded-lg bg-white mb-6"
                style={{ minHeight: "27rem" }}
              >
                <div className="grid grid-cols-7 gap-2">
                  {values.contacts.map((contact, index) => (
                    <React.Fragment key={contact.id}>
                      {/* Email Field - 4/7 columns */}
                      <div className="col-span-4 relative mb-1">
                        <div>
                          <Field
                            type="email"
                            name={`contacts.${index}.email`}
                            placeholder={t(
                              "assessments.feedback360.email-placeholder"
                            )}
                            disabled={contact.disabled}
                            className={`w-full px-4 border rounded-lg shadow-md ${
                              contact.disabled ? "bg-gray-200" : ""
                            } ${
                              hasFieldError(index, "email")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {!contact.disabled && (
                            <ErrorMessage
                              name={`contacts.${index}.email`}
                              component="p"
                              className="mt-1 text-sm text-red-500"
                            />
                          )}
                        </div>
                      </div>

                      {/* Type Field - 2/7 columns */}
                      <div className="col-span-2 relative">
                        <div>
                          <Field
                            as="select"
                            name={`contacts.${index}.type`}
                            disabled={contact.disabled}
                            className={`w-full px-4 border rounded-lg shadow-md ${
                              contact.disabled ? "bg-gray-200" : ""
                            } ${
                              hasFieldError(index, "type")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            <option value="" disabled className="text-gray-400">
                              {t("assessments.feedback360.select-placeholder")}
                            </option>
                            <option value="employee">
                              {t("assessments.feedback360.employee")}
                            </option>
                            <option value="manager">
                              {t("assessments.feedback360.manager")}
                            </option>
                            <option value="colleague">
                              {t("assessments.feedback360.colleague")}
                            </option>
                            <option value="businesspartner">
                              {t("assessments.feedback360.businesspartner")}
                            </option>
                            <option value="other">
                              {t("assessments.feedback360.other")}
                            </option>
                          </Field>
                          {!contact.disabled && (
                            <ErrorMessage
                              name={`contacts.${index}.type`}
                              component="p"
                              className="mt-1 text-sm text-red-500"
                            />
                          )}
                        </div>
                      </div>

                      {/* Date/Delete Column - 1/7 columns */}
                      <div className="col-span-1 flex justify-center">
                        {/* If it's a disabled contact with requestDate, show the date */}
                        {contact.disabled && contact.requestDate ? (
                          <div className="flex items-center ">
                            <span className="text-sm text-gray-600">
                              {contact.requestDate}
                            </span>
                          </div>
                        ) : /* If it's a non-disabled contact and has index >= 8, show the delete button */
                        !contact.disabled && index >= 8 ? (
                          <div className="flex items-center ">
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-gray-500 hover:text-red-500"
                              aria-label="Delete contact"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </div>
                        ) : (
                          /* Empty div to maintain the space */
                          <div className="h-8"></div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {(!feedbackResult || feedbackResult === null) && (
            <>
              {/* Add Contact Button */}
              <div className="flex justify-start flex-col items-start">
                {(() => {
                  const isLimitReached =
                    values.contacts.length >= FEEDBACK_360_LIMIT;
                  return (
                    <>
                      <Button
                        type="button"
                        // Conditionally apply the margin class
                        className={isLimitReached ? "mb-2" : "mb-8"}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        disabled={isLimitReached}
                        onClick={() => {
                          const newId =
                            values.contacts.length > 0
                              ? Math.max(...values.contacts.map((c) => c.id)) +
                                1
                              : 1;
                          push({ id: newId, email: "", type: "" });
                          setTimeout(scrollToBottom, 200);
                        }}
                      >
                        {t("assessments.feedback360.add-button")}
                      </Button>

                      {isLimitReached && (
                        // The error message now provides the larger bottom margin
                        <p className="text-sm text-red-500 mb-8">
                          {t("assessments.feedback360.limit_reached")}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </>
      )}
    </FieldArray>
  );
};

export default ContactList;
